import * as React from 'react';

//other deps
import _ from 'lodash';
import { toast } from 'react-toastify';
import { Localized } from '@fluent/react';
import { FluentBundle } from '@fluent/bundle';

//api
import * as api from 'app/api';

//redux
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';

//schemes
import * as schemes from '../../../schemes';

//components
import Builder from 'app/containers/Builder';

//types
import { RootState } from 'app/reducers';
import { DocumentAttributesMap, DocumentTypeEnum, EmptyCar, isStsMainPage, isStsOwnerPage } from 'app/models';
import { Field, SchemeProps } from 'app/containers/Builder/props';
import { EmptyCarActions } from 'app/actions';
import { RecognizeApiModel } from 'app/api/types';

//utils
import { changeDateFormat } from 'app/utils';

//helpers
import { mapTrailerTypes, mapTransportTypes, mapTransportBrands } from '../../../../common/common.helpers';

//constants
import { documentLocalizedLabelMap, documentTypeMap } from './certificateItem.constants';

//images
const transportImage = require('src/assets/icons/car_avatar.png');
const trailerImage = require('src/assets/icons/trailer_avatar.png');

//----------------------------------------------------------
// CertificateItem
//----------------------------------------------------------
class CertificateItem extends React.Component<CertificateItem.Props, CertificateItem.State> {
	private builder: Builder | null = null;

	public state: CertificateItem.State = {
		scheme: _.cloneDeep(schemes.certificate),
		loading: false,
	};

	constructor(props: CertificateItem.Props) {
		super(props);
	}

	private getLocalizedMessage = (id: string): string => {
		const { locales } = this.props;
		if (!locales) return '';

		const [locale] = locales;
		return locale.getMessage(id)?.value?.toString() || '';
	};

	public componentDidMount() {
		this.updateSchemeWithProps();
	}

	private updateSchemeWithProps = async () => {
		const { scheme } = this.state;
		const { certificateType, transportTypes, trailerTypes, brands } = this.props;

		const transportKindInput = scheme[0] as SchemeProps.Select<string>;
		const transportBrandInput = scheme[1] as SchemeProps.Select<string>;
		const transportModelInput = scheme[2] as SchemeProps.Input;

		if (certificateType === 'trailer') {
			if (trailerTypes) transportKindInput.choices = mapTrailerTypes(trailerTypes);

			transportKindInput.label = 'transports-certificate-trailer-kind';
			transportBrandInput.label = 'transports-certificate-trailer-brand';
			transportModelInput.label = 'transports-certificate-trailer-model';
		}

		if (certificateType === 'transport' && transportTypes) {
			transportKindInput.choices = mapTransportTypes(transportTypes);
		}

		if (brands) {
			transportBrandInput.choices = mapTransportBrands(brands);
		}

		this.setState({ scheme });
		await Promise.delay(70);

		this.updateBuilderValues();
	};

	private updateBuilderValues = () => {
		const { builder } = this;
		const { scheme } = this.state;
		const { certificateType, car } = this.props;

		if (!builder || !car?.documents[certificateType]) return;

		const allowedInputFields = ['model', 'vin', 'number', 'color'];
		const allowedSelectFields = ['kind', 'brand', 'manufactureYear'];
		const allowedDateFields = ['issuedAt'];

		[...allowedInputFields, ...allowedDateFields].forEach((fieldName) => {
			const ref = builder.formRefs.get(fieldName);
			const value = car.documents[certificateType]?.[fieldName as keyof EmptyCar.Certificate] || null;

			if (!ref || !value) return;
			ref.updateValue(value);
		});

		allowedSelectFields.forEach((fieldName) => {
			const ref = builder.formRefs.get(fieldName);
			const value = car.documents[certificateType]?.[fieldName as keyof EmptyCar.Certificate] || null;

			if (!ref || !value) return;

			const getChoice = (choices: Field.Choice<string>[], value: string) => {
				return choices.find((item) => item.value === value) || null;
			};

			const schemeMap: Record<string, number> = {
				kind: 0,
				brand: 1,
			};

			switch (fieldName) {
				case 'manufactureYear':
					ref.updateValue({ label: value, value });
					break;

				case 'kind':
				case 'brand': {
					const schemeIndex = schemeMap[fieldName];
					const { choices = [] } = scheme[schemeIndex] as SchemeProps.Select<string>;
					const choice = getChoice(choices, value as string);

					if (choice) {
						ref.updateValue(choice);
					}
					break;
				}

				default:
					break;
			}
		});
	};

	private mapRecognizedDocumentFields = <T extends keyof DocumentAttributesMap>({
		documentType,
		attributes,
	}: RecognizeApiModel.ProcessResponseData<T>) => {
		const { certificateType } = this.props;

		let basePatch: Partial<EmptyCar.Base> = {};
		let certificatePatch: Partial<EmptyCar.Certificate> = {};

		if (isStsOwnerPage(documentType, attributes)) {
			const { number, issuedAt } = attributes;

			certificatePatch = {
				number: number?.value || undefined,
				issuedAt: changeDateFormat('DD.MM.YYYY', 'YYYY-MM-DD', issuedAt?.value) || undefined,
			};
		} else if (isStsMainPage(documentType, attributes)) {
			const { number, vin, color, manufactureYear, model, vehicleType, ident } = attributes;

			const identValue = ident?.value || undefined;
			basePatch = certificateType === 'trailer' ? { trailerIdent: identValue } : { ident: identValue };

			certificatePatch = {
				number: number?.value || undefined,
				vin: vin?.value || undefined,
				color: color?.value || undefined,
				model: model?.value || undefined,
				kind: vehicleType?.kind?.id || undefined,
				brand: model?.brand?.id || undefined,
				manufactureYear: manufactureYear?.value || undefined,
			};
		}

		return { basePatch, certificatePatch };
	};

	private documentRecognizeProcess = async (name: string, value: any) => {
		const documentType = documentTypeMap[name];
		if (!documentType || !value) return;

		let base: Partial<EmptyCar.Base> = {};
		let certificate: Partial<EmptyCar.Certificate> = {};

		let failedMessage: string | null = null;
		const documentLocalizedName = documentLocalizedLabelMap[name];

		try {
			this.setState({ loading: true });

			const data = await api.recognizeProcess({
				documentType: documentType,
				documentFile: value,
			});

			if (data.documentType === DocumentTypeEnum.Invalid) {
				failedMessage = documentLocalizedName;
			} else if (documentType !== data.documentType) {
				failedMessage = documentLocalizedName;
			} else {
				const result = this.mapRecognizedDocumentFields(data);

				base = result.basePatch;
				certificate = result.certificatePatch;
			}
		} catch (error) {
			failedMessage = documentLocalizedName;
		} finally {
			this.setState({ loading: false });
		}

		if (failedMessage) {
			const title = this.getLocalizedMessage('profile-alert-docs-recognize-failed');
			const description = this.getLocalizedMessage(failedMessage);
			const errorMessage = `${title} ${description}`;

			toast.error(errorMessage, {
				autoClose: 5000,
				position: toast.POSITION.BOTTOM_CENTER,
			});
		} else {
			toast.success(this.getLocalizedMessage('profile-alert-updated'), {
				position: toast.POSITION.BOTTOM_CENTER,
			});
		}

		if (!Object.keys({ ...base, ...certificate }).length) return;

		if (Object.keys(base).length) {
			const { updateBaseData } = this.props;

			if (updateBaseData) updateBaseData(base);
		}

		if (Object.keys(certificate).length) {
			const { certificateType, updateTransportData, updateTrailerData } = this.props;
			const updateFunc = certificateType === 'transport' ? updateTransportData : updateTrailerData;

			if (updateFunc) updateFunc(certificate);
		}

		this.updateBuilderValues();
		this.builder?.isValid();
	};

	private onUpdate = (name: string, value: any) => {
		const { certificateType, updateTransportData, updateTrailerData } = this.props;
		const updateFunc = certificateType === 'transport' ? updateTransportData : updateTrailerData;

		if (!updateFunc || !name) return;

		updateFunc({ [name]: value });

		//[id-engine] recognize document
		this.documentRecognizeProcess(name, value);
	};

	public render() {
		const { scheme, loading } = this.state;
		const { certificateType } = this.props;

		const certificateBuilderProps: Builder['props'] = {
			getRef: (node: Builder | null) => {
				this.builder = node;
			},
			onUpdate: this.onUpdate,
			scheme: scheme.map((el: SchemeProps.Union) => ({
				...el,
				disabled: loading,
			})),
		};

		const image = certificateType === 'transport' ? transportImage : trailerImage;

		return (
			<div className='certificate item'>
				<div className='icons'>
					<div className='icon'>
						<img src={image} />
					</div>
				</div>

				<div className='titlebar'>
					<div className='titles'>
						<div className='header'>
							<Localized id={`transports-certificates-type-${certificateType}`} />
						</div>
					</div>
				</div>

				<div className='form'>
					<Builder {...certificateBuilderProps} />
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ common }: RootState) => ({
	locales: common.bundlesLocales,
	brands: common.brands,
	trailerTypes: common.trailers,
	transportTypes: common.transports,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			updateBaseData: EmptyCarActions.updateBaseData,
			updateTransportData: EmptyCarActions.updateTransportData,
			updateTrailerData: EmptyCarActions.updateTrailerData,
		},
		dispatch,
	);

export default connect<CertificateItem.StateProps, CertificateItem.DispatchProps, CertificateItem.ExternalProps>(
	mapStateToProps,
	mapDispatchToProps,
)(CertificateItem);

export namespace CertificateItem {
	export type Props = StateProps & DispatchProps & ExternalProps;

	// Props from redux mapState
	export interface StateProps {
		locales?: FluentBundle[];
		brands?: RootState.Common['brands'];
		trailerTypes?: RootState.Common['trailers'];
		transportTypes?: RootState.Common['transports'];
	}

	// Dispatch properties function from redux
	export interface DispatchProps {
		updateBaseData?: (params: Partial<EmptyCar.Base>) => void;
		updateTransportData?: (params: Partial<EmptyCar.Certificate>) => void;
		updateTrailerData?: (params: Partial<EmptyCar.Certificate>) => void;
	}

	// Props from parent element e.g <Cmp custom={true} />
	export interface ExternalProps {
		car?: EmptyCar.Data | null;
		certificateType: 'trailer' | 'transport';
	}

	// Main component state
	export interface State {
		scheme: SchemeProps.Union[];
		loading: boolean;
	}
}
